import React from "react";
import SimpleModal from "../../../UI/Modals/SimpleModal/SimpleModal";
import Checkbox from "../../../UI/Checkbox/Checkbox";
import Button from "../../../UI/Buttons/Button/Button";
import styles from "./ArchivedColumnFilters.module.scss";

// All unique archived statuses (combined from orders and RFZO, no duplicates)
const ALL_STATUSES = [
  "TAKEN",
  "REJECTED",
  "CANCELED",
  "SUSPENDED",
  "REQUEST_EXPIRED",
  "PROCESSED_WITHOUT_CONTENT",
  "REJECT_REQUESTED",
  "REJECTED_OUT_OF_STOCK",
  "REJECTED_WITHOUT_CONTENT",
  "EXPIRED",
] as const;

const STATUS_LABELS: Record<string, string> = {
  TAKEN: "Preuzeto",
  REJECTED: "Odbijeno",
  CANCELED: "Otkazano",
  SUSPENDED: "Propušteno",
  REQUEST_EXPIRED: "Zahtev istekao",
  PROCESSED_WITHOUT_CONTENT: "Obrađeno bez sadržaja",
  REJECT_REQUESTED: "Zatraženo odbijanje",
  REJECTED_OUT_OF_STOCK: "Odbijeno - nema na stanju",
  REJECTED_WITHOUT_CONTENT: "Odbijeno bez sadržaja",
  EXPIRED: "Isteklo",
};

interface ArchivedColumnFiltersProps {
  filters: { status: string };
  setFilters: (filters: { status: string }) => void;
  hideModal: () => void;
}

export const ArchivedColumnFilters: React.FC<ArchivedColumnFiltersProps> = ({
  filters,
  setFilters,
  hideModal,
}) => {
  const currentStatuses =
    filters?.status === "ALL" || !filters?.status
      ? []
      : filters.status.split(",").filter(Boolean);

  const isAllSelected = filters?.status === "ALL";

  const isStatusSelected = (status: string) => {
    return isAllSelected || currentStatuses.includes(status);
  };

  const toggleStatus = (status: string) => {
    if (isAllSelected) {
      // When ALL is selected, clicking a status deselects everything except that status
      setFilters({ status: status });
      return;
    }

    let newStatuses: string[];
    if (currentStatuses.includes(status)) {
      newStatuses = currentStatuses.filter((s) => s !== status);
    } else {
      newStatuses = [...currentStatuses, status];
    }

    // If all statuses are selected, switch to ALL
    if (newStatuses.length === ALL_STATUSES.length) {
      setFilters({ status: "ALL" });
    } else if (newStatuses.length === 0) {
      setFilters({ status: "" });
    } else {
      setFilters({ status: newStatuses.join(",") });
    }
  };

  const selectAll = () => {
    setFilters({ status: "ALL" });
  };

  const deselectAll = () => {
    setFilters({ status: "" });
  };

  return (
    <SimpleModal
      className={styles.filtersModal}
      backdropClickHandler={hideModal}
      show={false}
    >
      <div className={styles.segment}>
        <div className={styles.header}>Filter po statusu</div>

        {/* Select All checkbox */}
        <div className={styles.selectAllSection}>
          <div className={styles.selectItem}>
            <Checkbox
              value={isAllSelected}
              onChange={() => (isAllSelected ? deselectAll() : selectAll())}
            />
            <span className={styles.allLabel}>Svi</span>
          </div>
        </div>

        {/* All statuses in a single flat list */}
        <div className={styles.statusFilter}>
          {ALL_STATUSES.map((status) => (
            <div key={status} className={styles.selectItem}>
              <Checkbox
                value={isStatusSelected(status)}
                onChange={() => toggleStatus(status)}
              />
              {STATUS_LABELS[status]}
            </div>
          ))}
        </div>
      </div>

      <Button buttonType="blue" onClick={hideModal}>
        Primeni
      </Button>
    </SimpleModal>
  );
};

export default ArchivedColumnFilters;
